import React, { useEffect, useMemo, useState, useCallback } from "react";
import PageTitle from "../common/PageTitle";
import {
  Alert,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  ThemeProvider,
  Typography,
  TextField,
  createTheme,
  Grid,
  Button,
  MenuItem,
  Select,
  CircularProgress,
  Backdrop,
  FormControl,
  InputLabel,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import CloseIcon from "@mui/icons-material/Close";
import { makeStyles } from "@mui/styles";
import dayjs from "dayjs";
import moment from "moment";
import Datetime from "react-datetime";
import {
  DataGrid,
  GridToolbarContainer,
  GridToolbarColumnsButton,
  GridToolbarDensitySelector,
  GridToolbarFilterButton,
} from "@mui/x-data-grid";
import { useDispatch, useSelector } from "react-redux";
import {
  getAdminAuditLogs,
  clearAdminAuditLogs,
} from "../../redux/actions/adminPortal_auditLogs";
import { StyledDataGrid } from "../../pages/CustomDataGrid";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";

const drawerWidth = 240;

// Custom styles
const useStyles = makeStyles({
  borderedGreen: {
    borderLeft: "3px solid green",
    boxShadow: "2px -1px 4px -3px #686868",
    margin: "4px 4px 1px 4px !important",
  },
  borderedRed: {
    borderLeft: "3px solid red",
    boxShadow: "2px -1px 4px -3px #686868",
    margin: "4px 4px 1px 4px !important",
  },
});

// Custom theme for compact DataGrid
const muiTheme = createTheme({
  components: {
    MuiDataGrid: {
      styleOverrides: {
        root: {
          "& .MuiDataGrid-row": {
            minHeight: "auto",
          },
        },
      },
      defaultProps: {
        density: "compact",
      },
    },
  },
});

function CustomToolbar() {
  return (
    <GridToolbarContainer
      sx={{
        "& .MuiButton-root": {
          fontSize: {
            xs: "12px",
            sm: "14px",
            md: "14px",
          },
          minWidth: "auto",
        },
        "& .MuiSvgIcon-root": {
          fontSize: {
            xs: "16px",
            sm: "18px",
          },
        },
      }}
    >
      {/* <GridToolbarColumnsButton />
      <GridToolbarDensitySelector />
      <GridToolbarFilterButton /> */}
    </GridToolbarContainer>
  );
}

function AdminAuditLog({ colorThem }) {
  const theme = useTheme();
  const classes = useStyles();
  const dispatch = useDispatch();
  const state = useSelector((state) => state);

  const isXs = useMediaQuery(theme.breakpoints.only("xs"));
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // Initial date setup
  const getTodayStart = () => moment().startOf("day");
  const getTodayEnd = () => moment().endOf("day");

  const [fromDate, setFromDate] = useState(getTodayStart);
  const [toDate, setToDate] = useState(getTodayEnd);
  const [searchDestination, setSearchDestination] = useState("");
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [loader, setLoader] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(100);
  const [username, setUsername] = useState("");
  const [url, setUrl] = useState();
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ action: "", username: "", url: "" });

  // Clear-logs dialog state — admin-only destructive flow.
  // Requires both a valid cutoff date AND user typing "DELETE" verbatim,
  // then a final native confirm() before the API call fires.
  const CLEAR_CONFIRM_PHRASE = "DELETE";
  const [clearOpen, setClearOpen] = useState(false);
  const [clearBeforeDate, setClearBeforeDate] = useState(
    moment().subtract(30, "days").startOf("day")
  );
  const [clearConfirmText, setClearConfirmText] = useState("");
  const [clearLoading, setClearLoading] = useState(false);
  const [clearError, setClearError] = useState("");

  // Fetch data on mount
  useEffect(() => {
    handleSearch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFromDateChange = (date) => {
    if (moment.isMoment(date)) setFromDate(date);
  };

  const handleToDateChange = (date) => {
    if (moment.isMoment(date)) setToDate(date);
  };

  // Fetch audit logs
  const handleSearch = () => {
    const data = {
      from_date: dayjs(fromDate).isValid()
        ? dayjs(fromDate).format("YYYY-MM-DD HH:mm")
        : "",
      to_date: dayjs(toDate).isValid()
        ? dayjs(toDate).format("YYYY-MM-DD HH:mm")
        : "",
      action: filters.action,
      url: filters.url,
      username: filters.username,
    };
    setLoader(true);

    dispatch(getAdminAuditLogs(data)).finally(() => setLoader(false));
  };

  const handleClearOpen = () => {
    setClearBeforeDate(moment().subtract(30, "days").startOf("day"));
    setClearConfirmText("");
    setClearError("");
    setClearOpen(true);
  };

  const handleClearClose = () => {
    if (clearLoading) return;
    setClearOpen(false);
    setClearConfirmText("");
    setClearError("");
  };

  const isClearDateValid =
    moment.isMoment(clearBeforeDate) &&
    clearBeforeDate.isValid() &&
    clearBeforeDate.isSameOrBefore(moment());

  const isClearArmed =
    isClearDateValid &&
    clearConfirmText === CLEAR_CONFIRM_PHRASE &&
    !clearLoading;

  const handleClearSubmit = () => {
    if (!isClearArmed) return;
    const beforeStr = clearBeforeDate.format("YYYY-MM-DD HH:mm:ss");
    const proceed = window.confirm(
      `Final confirmation:\n\nPermanently delete ALL API audit log entries created before ${beforeStr}?\n\nThis cannot be undone. Click OK to proceed, Cancel to abort.`
    );
    if (!proceed) return;
    setClearLoading(true);
    setClearError("");
    dispatch(
      clearAdminAuditLogs(
        beforeStr,
        () => {
          setClearLoading(false);
          setClearOpen(false);
          setClearConfirmText("");
          handleSearch();
        },
        (msg) => {
          setClearLoading(false);
          setClearError(msg || "Failed to clear logs");
        }
      )
    );
  };

  // Reset date & auto-search
  const handleReset = () => {
    const start = moment().startOf("day");
    const end = moment().endOf("day");
    setFromDate(start);
    setToDate(end);
    setFilters({ action: "", username: "", url: "" });

    // Trigger API call immediately
    const data = {
      from_date: start.format("YYYY-MM-DD HH:mm"),
      to_date: end.format("YYYY-MM-DD HH:mm"),
    };
    setLoader(true);
    dispatch(getAdminAuditLogs(data)).finally(() => setLoader(false));
  };

  // Prepare table rows
  const rows = useMemo(() => {
    const logs = state?.getAdminAuditLogs?.auditLogs || [];
    return logs.map((item, index) => {
      let parsedData = {};
      try {
        parsedData = JSON.parse(item.data || "{}");
      } catch (e) {
        console.warn("Invalid JSON in audit log data:", e);
      }

      return {
        ...parsedData,
        id: item?.id,
        user_name: item?.user_name,
        action: item?.action,
        created_at: item?.created_at,
        timestamp: item?.created_at,
        ip_address: item?.source_ip,
        url: item?.url,
        auditlog_id: item?.id,
        data: item?.data,
      };
    });
  }, [state?.getAdminAuditLogs?.auditLogs]);

  // Filter rows based on search
  // const filteredRows = useMemo(() => {
  //   if (!search) return rows;
  //   return rows.filter(
  //     (row) =>
  //       row.user_name?.toLowerCase().includes(search.toLowerCase()) ||
  //       row.action?.toLowerCase().includes(search.toLowerCase()) ||
  //       row.ip_address?.toLowerCase().includes(search.toLowerCase()) ||
  //       row.url?.toLowerCase().includes(search.toLowerCase()),
  //   );
  // }, [rows, search]);

  const filteredRows = useMemo(() => {
    return rows.filter((row) => {
      // ✅ Action
      const matchAction = filters.action
        ? row.action?.toUpperCase() === filters.action
        : true;

      // ✅ Username (actor + target दोनों cover)
      const matchUsername = filters.username
        ? row.user_name?.toLowerCase().includes(filters.username.toLowerCase())
        : true;

      // ✅ URL
      const matchUrl = filters.url
        ? row.url?.toLowerCase() === filters.url.toLowerCase()
        : true;

      return matchAction && matchUsername && matchUrl;
    });
  }, [rows, filters]);

  // Pagination Logic
  const totalPages = Math.ceil(filteredRows.length / pageSize);
  const currentRows = filteredRows.slice(
    currentPage * pageSize,
    currentPage * pageSize + pageSize,
  );
  const currentPageRowIds = currentRows.map((row) => row.auditlog_id);

  // Handle Checkbox Selection
  const handleSelectionChange = (newSelection) => {
    setLoading(true);
    setTimeout(() => {
      const updated = new Set(selectedRows);
      currentPageRowIds.forEach((id) => updated.delete(id));
      newSelection.forEach((id) => updated.add(id));
      setSelectedRows(updated);
      setLoading(false);
    }, 50);
  };

  // Get Current Page Selected
  const getCurrentPageSelected = () => {
    if (selectedRows instanceof Set) {
      return currentPageRowIds.filter((id) => selectedRows.has(id));
    } else if (Array.isArray(selectedRows)) {
      return currentPageRowIds.filter((id) => selectedRows.includes(id));
    }
    return [];
  };

  // Select All on Current Page
  const handleSelectAllOnPage = () => {
    const updated = new Set(selectedRows);
    currentPageRowIds.forEach((id) => updated.add(id));
    setSelectedRows(updated);
  };

  // Clear Selection on Current Page
  const handleClearSelectionOnPage = () => {
    const updated = new Set(selectedRows);
    currentPageRowIds.forEach((id) => updated.delete(id));
    setSelectedRows(updated);
  };

  // Pagination Handlers
  const handleNext = () => {
    if (currentPage < totalPages - 1) setCurrentPage(currentPage + 1);
  };
  const handlePrevious = () => {
    if (currentPage > 0) setCurrentPage(currentPage - 1);
  };
  const handlePageSizeChange = (e) => {
    setPageSize(Number(e.target.value));
    setCurrentPage(0);
  };

  // DataGrid columns
  const columns = [
    {
      field: "action",
      headerName: "Action",
      width: isXs ? 60 : 90,
      renderHeader: () => (
        <Typography
          sx={{
            fontSize: "0.8rem",
            fontWeight: "bold",
            color: "white!important",
          }}
        >
          Action
        </Typography>
      ),
      renderCell: (params) => (
        <Typography sx={{ fontSize: "0.8rem" }}>{params.row.action}</Typography>
      ),
    },
    {
      field: "user_name",
      headerName: "User Name",
      width: 120,
      renderHeader: () => (
        <Typography
          sx={{
            fontSize: "0.8rem",
            fontWeight: "bold",
            color: "white!important",
          }}
        >
          User Name
        </Typography>
      ),
      renderCell: (params) => (
        <Typography sx={{ fontSize: "0.8rem" }}>
          {params.row.user_name}
        </Typography>
      ),
    },
    {
      field: "created_at",
      headerName: "Date",
      width: 120,
      renderHeader: () => (
        <Typography
          sx={{
            fontSize: "0.8rem",
            fontWeight: "bold",
            color: "white!important",
          }}
        >
          Date
        </Typography>
      ),
      renderCell: (params) => {
        if (!params.value) return "—";
        return (
          <Typography sx={{ color: "blue", fontSize: "0.8rem" }}>
            {moment(params.value).format("DD/MM/YYYY")}
          </Typography>
        );
      },
    },
    {
      field: "timestamp",
      headerName: "Time",
      width: 100,
      renderHeader: () => (
        <Typography
          sx={{
            fontSize: "0.8rem",
            fontWeight: "bold",
            color: "white!important",
          }}
        >
          Time
        </Typography>
      ),
      //   renderCell: (params) => {
      //   if (params.value !== null) {
      //     const date = new Date(params.value);          
      //     var hours = date.getUTCHours();
      //     var minutes = date.getUTCMinutes();
      //     var seconds = date.getUTCSeconds();

        

      //     // Formatting single-digit hours/minutes/seconds with leading zero if needed
      //     hours = (hours < 10 ? "0" : "") + hours;
      //     minutes = (minutes < 10 ? "0" : "") + minutes;
      //     seconds = (seconds < 10 ? "0" : "") + seconds;
      //     return (
      //       <>
      //         <div style={{ display: "flex", flexDirection: "column" }}>
              
      //           <span
      //             style={{ color: "green", fontSize: "calc(0.55rem + 0.2vw)" }}
      //           >
      //             {`${hours}:${minutes}:${seconds}`}
      //           </span>
      //         </div>
      //       </>
      //     );
      //   }
      // },
      renderCell: (params) => {
        if (!params.value) return "—";
        return (
          <Typography sx={{ color: "green", fontSize: "0.8rem" }}>
            {moment(params.value).format("HH:mm:ss")}
          </Typography>
        );
      },
    },
    {
      field: "ip_address",
      headerName: "IP Address",
      width: 130,
      renderHeader: () => (
        <Typography
          sx={{
            fontSize: "0.8rem",
            fontWeight: "bold",
            color: "white!important",
          }}
        >
          IP Address
        </Typography>
      ),
      renderCell: (params) => (
        <Typography sx={{ fontSize: "0.8rem", color: "blue" }}>
          {params.value}
        </Typography>
      ),
    },
    {
      field: "url",
      headerName: "URL",
      width: 180,

      renderHeader: () => (
        <Typography
          sx={{
            fontSize: "0.8rem",
            fontWeight: "bold",
            color: "white!important",
          }}
        >
          URL
        </Typography>
      ),
      valueFormatter: (params) => {
        const cleaned = (params.value || "")
          .split("/")
          .filter((part) => part && part !== "." && part !== "..")
          .pop();
        return cleaned || "";
      },
      renderCell: (params) => (
        <Typography sx={{ fontSize: "0.8rem" }}>
          {params.formattedValue}
        </Typography>
      ),
    },
    {
      field: "data",
      headerName: "Data",
      minWidth: 1000,
      flex: 1,
      renderHeader: () => (
        <Typography
          sx={{
            fontSize: "0.8rem",
            fontWeight: "bold",
            color: "white!important",
          }}
        >
          Data
        </Typography>
      ),
    },
  ];

  return (
    <div className={`App ${colorThem}`}>
      <div className="contant_box">
        <Box
          className="right_sidebox mobile_top_pddng"
          component="main"
          sx={{
            flexGrow: 1,
            p: 3,
            width: { sm: `calc(100% - ${drawerWidth}px)` },
          }}
        >
          {/* Title row — Clear Logs button right-aligned next to the title */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: "10px",
              mb: 2,
            }}
          >
            <div className="cntnt_title">
              <div
                className="d-flex align-items-center"
                style={{ gap: "8px" }}
              >
                <PageTitle title="API Audit Logs" />

                {filteredRows.length > 0 && (
                  <Typography
                    variant="subtitle2"
                    className="calltotalcall"
                    sx={{
                      color: "text.secondary",
                      fontSize: { xs: "13px", sm: "15px" },
                      fontWeight: 800,
                    }}
                  >
                    ({filteredRows.length})
                  </Typography>
                )}
              </div>
            </div>

            <Button
              variant="contained"
              color="error"
              startIcon={<DeleteForeverIcon />}
              onClick={handleClearOpen}
              sx={{ textTransform: "none" }}
            >
              Clear Logs
            </Button>
          </Box>

          {/* Filters row */}
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "row", md: "row" },
              justifyContent: { xs: "left", md: "flex-end" },
              flexWrap: "wrap",
              alignItems: { xs: "left", md: "center" },
              mb: 2,
              textAlign: { xs: "center", md: "left" },
            }}
          >
            <Grid container spacing={2} className="pb-2">
              {/* Action Dropdown */}
              <Grid item xs={12} md={4}>
                <FormControl
  fullWidth
  size="small"
  sx={{
    "& .MuiOutlinedInput-root": {
      height: "46px",
      display: "flex",
      alignItems: "center",
    },
  }}
>
  <InputLabel>Action</InputLabel>

  <Select
    value={filters.action}
    label="Action"
    onChange={(e) =>
      setFilters((prev) => ({
        ...prev,
        action: e.target.value,
      }))
    }
  >    
    <MenuItem value="GET">GET</MenuItem>
    <MenuItem value="POST">POST</MenuItem>
    <MenuItem value="PUT">PUT</MenuItem>
    <MenuItem value="DELETE">DELETE</MenuItem>
  </Select>
</FormControl>
              </Grid>

              {/* Username Search */}
              <Grid item xs={12} md={4}>
                <TextField
                  label="Username"
                  size="small"
                  value={filters.username}
                  onChange={(e) => {
                    setFilters((prev) => ({
                      ...prev,
                      username: e.target.value,
                    }));
                    setCurrentPage(0);
                  }}
                 fullWidth
              sx={{
                "& .MuiOutlinedInput-root": {
                  height: "46px",   // ✅ fixed height
                },
              }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          onClick={() => {
                            setFilters((prev) => ({
                              ...prev,
                              username: "",
                            }));
                            setCurrentPage(0);
                          }}
                        >
                          <ClearIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              {/* Second Field (rename recommended) */}
              <Grid item xs={12} md={4}>
                <TextField
                  label="URL"
                  size="small"
                  value={filters.url}
                  onChange={(e) => {
                    setFilters((prev) => ({
                      ...prev,
                      url: e.target.value,
                    }));
                    setCurrentPage(0);
                  }}
                  fullWidth
               sx={{
                "& .MuiOutlinedInput-root": {
                  height: "46px",   // ✅ fixed height
                },
              }}
                  InputProps={{
                    endAdornment: search && (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          onClick={() => {
                            setFilters((prev) => ({
                              ...prev,
                              url: "",
                            }));
                            setCurrentPage(0);
                          }}
                        >
                          <ClearIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
            </Grid>

            <Grid container spacing={1}>
              <Grid item xs={12}>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: { xs: "row", sm: "row" },
                    flexWrap: "wrap",
                    alignItems: { xs: "center", md: "flex-end" },
                    justifyContent: { xs: "flex", md: "flex-end" },
                    gap: 2,
                    width: { xs: "100%", md: "auto" },
                  }}
                >
                  {/* From Date */}
                  <Box sx={{ width: { xs: "100%", sm: "220px" } }}>
                    <Typography
                      sx={{ fontSize: 14, fontWeight: 500, textAlign: "left" }}
                    >
                      From Date:
                    </Typography>
                    <Datetime
                      className="datefield_select new_input m-0"
                      value={fromDate}
                      onChange={handleFromDateChange}
                      dateFormat="DD/MM/YYYY"
                      timeFormat="HH:mm"
                    />
                  </Box>

                  {/* To Date */}
                  <Box sx={{ width: { xs: "100%", sm: "220px" } }}>
                    <Typography
                      sx={{ fontSize: 14, fontWeight: 500, textAlign: "left" }}
                    >
                      To Date:
                    </Typography>
                    <Datetime
                      className="datefield_select new_input m-0"
                      value={toDate}
                      onChange={handleToDateChange}
                      dateFormat="DD/MM/YYYY"
                      timeFormat="HH:mm"
                    />
                  </Box>

                  {/* Buttons */}
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: { xs: "flex-end", md: "flex-end" },
                      width: { xs: "100%", md: "auto" },
                    }}
                  >
                    <IconButton
                      className="filter_search_btn"
                      style={{
                        marginLeft: "0 !important",
                        background: "green !important",
                      }}
                      onClick={handleSearch}
                    >
                      Search&nbsp; <SearchIcon />
                    </IconButton>
                    <IconButton
                      className="filter_reset_btn"
                      style={{
                        marginLeft: "0 !important",
                        backgroundColor: "black",
                      }}
                      onClick={handleReset}
                    >
                      Reset&nbsp; <RestartAltIcon />
                    </IconButton>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Box>

          {/* Table Section with Filter and Pagination */}
          <Box sx={{ width: "100%" }}>
            {/* Toolbar */}

            {/* <Grid container >
             
              <Grid item xs={12} md={5}>
                <TextField
                  label="Search..."
                  size="small"
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setCurrentPage(0);
                  }}
                  fullWidth
                  InputProps={{
                    endAdornment: search && ( // 👈 sirf tab show jab value ho
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          onClick={() => {
                            setSearch("");
                            setCurrentPage(0);
                          }}
                        >
                          <ClearIcon
                            fontSize="small"
                            sx={{
                              background: "#333!important",
                              borderRadius: "20px",
                            }}
                          />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid> 

             
               <Grid item xs={12} md={7}>
                <Grid container spacing={1}>
                  
                  <Grid item xs={12} sm={12} md={2}>
                    <Select
                      size="small"
                      value={pageSize}
                      onChange={handlePageSizeChange}
                      fullWidth
                    >
                      <MenuItem value={100}>100</MenuItem>
                      <MenuItem value={500}>500</MenuItem>
                      <MenuItem value={1000}>1000</MenuItem>
                    </Select>
                  </Grid>

                
                  <Grid item xs={12} sm={12} md={2}>
                    <Button
                      className="darkbtnout"
                      variant="outlined"
                      onClick={handleSelectAllOnPage}
                      fullWidth
                      sx={{
                        fontSize: isMobile ? "15px" : "10px",
                        height: "40px",
                        alignItems: "center",
                        textAlign: "center",
                      }}
                    >
                      Select All
                    </Button>
                  </Grid>

                
                  {selectedRows.size > 0 && (
                    <Grid item xs={12} sm={12} md={2}>
                      <Button
                        className="darkbtnout"
                        variant="outlined"
                        onClick={handleClearSelectionOnPage}
                        fullWidth
                        sx={{
                          fontSize: isMobile ? "15px" : "10px",
                          height: "40px",
                          alignItems: "center",
                          textAlign: "center",
                        }}
                      >
                        Clear
                      </Button>
                    </Grid>
                  )}

                 
                  <Grid item xs={12} sm={12} md={2}>
                    <Button
                      className="prevbtnblk"
                      variant="contained"
                      onClick={handlePrevious}
                      disabled={currentPage === 0}
                      fullWidth
                      sx={{
                        fontSize: isMobile ? "15px" : "10px",
                        height: "40px",
                        alignItems: "center",
                        textAlign: "center",
                      }}
                    >
                      Previous
                    </Button>
                  </Grid>

                 
                  <Grid item xs={12} sm={12} md={2}>
                    <Button
                      variant="contained"
                      onClick={handleNext}
                      disabled={currentPage >= totalPages - 1}
                      fullWidth
                      sx={{
                        fontSize: isMobile ? "15px" : "10px",
                        height: "40px",
                        alignItems: "center",
                        textAlign: "center",
                      }}
                    >
                      Next
                    </Button>
                  </Grid>

                 
                  <Grid item xs={12} sm={12} md={2}>
                    <Typography
                      sx={{ whiteSpace: "nowrap", pt: 1, textAlign: "center" }}
                    >
                      Page {currentPage + 1} of {totalPages}
                    </Typography>
                  </Grid>
                </Grid>
              </Grid> 
            </Grid> */}

            {/* DataGrid */}
            <DataGrid
              rows={rows}
              columns={columns}
              checkboxSelection
              disableRowSelectionOnClick
              // getRowId={(row) => row.auditlog_id}
              // rowSelectionModel={getCurrentPageSelected()}
              // onRowSelectionModelChange={handleSelectionChange}
              density="compact"
              autoHeight
              slots={{
                toolbar: CustomToolbar,
              }}
            />

            {/* Loader */}
            <Backdrop
              sx={{
                color: "#fff",
                zIndex: (theme) => theme.zIndex.drawer + 1,
              }}
              open={loading}
            >
              <Box textAlign="center">
                <CircularProgress color="inherit" />
                <Typography sx={{ mt: 2 }}>Loading audit logs...</Typography>
              </Box>
            </Backdrop>
          </Box>
        </Box>
      </div>

      {/* Clear Logs Confirmation Dialog */}
      <Dialog
        open={clearOpen}
        onClose={handleClearClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            color: "#d32f2f",
            fontWeight: 600,
          }}
        >
          <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <DeleteForeverIcon />
            Clear API Audit Logs
          </span>
          <IconButton onClick={handleClearClose} disabled={clearLoading}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Alert severity="error" sx={{ mb: 2 }}>
            <strong>This action cannot be undone.</strong> All API audit log
            entries with a timestamp <strong>before</strong> the date below
            will be permanently deleted from the database.
          </Alert>

          <Typography sx={{ fontSize: 14, fontWeight: 500, mb: 0.5, color: "#212121 !important" }}>
            Delete entries created before:
          </Typography>
          <Datetime
            className="datefield_select new_input m-0"
            value={clearBeforeDate}
            onChange={(val) => setClearBeforeDate(val)}
            dateFormat="DD/MM/YYYY"
            timeFormat="HH:mm"
            isValidDate={(current) => current.isSameOrBefore(moment(), "day")}
          />
          {!isClearDateValid && (
            <Typography sx={{ fontSize: 12, color: "#d32f2f", mt: 0.5 }}>
              Pick a valid date in the past or today.
            </Typography>
          )}

          <Box sx={{ mt: 2, p: 1.5, bgcolor: "#fff3e0", borderRadius: 1 }}>
            <Typography sx={{ fontSize: 13, color: "#5d4037 !important" }}>
              Cutoff:{" "}
              <strong>
                {isClearDateValid
                  ? clearBeforeDate.format("DD/MM/YYYY HH:mm:ss")
                  : "—"}
              </strong>
              <br />
              Logs with <code>created_at &lt;</code> this datetime will be
              deleted. The clear operation itself is recorded in the Portal
              Audit Log (so the trail of who-cleared-what survives).
            </Typography>
          </Box>

          <Typography sx={{ fontSize: 14, fontWeight: 500, mt: 2, mb: 0.5, color: "#212121 !important" }}>
            Type <strong>{CLEAR_CONFIRM_PHRASE}</strong> to confirm:
          </Typography>
          <TextField
            size="small"
            fullWidth
            value={clearConfirmText}
            onChange={(e) => setClearConfirmText(e.target.value)}
            placeholder={CLEAR_CONFIRM_PHRASE}
            disabled={clearLoading}
            error={
              clearConfirmText.length > 0 &&
              clearConfirmText !== CLEAR_CONFIRM_PHRASE
            }
            helperText={
              clearConfirmText.length > 0 &&
              clearConfirmText !== CLEAR_CONFIRM_PHRASE
                ? `Type ${CLEAR_CONFIRM_PHRASE} exactly (case-sensitive).`
                : " "
            }
          />

          {clearError && (
            <Alert severity="error" sx={{ mt: 1 }}>
              {clearError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={handleClearClose}
            disabled={clearLoading}
            variant="outlined"
            sx={{ textTransform: "none" }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleClearSubmit}
            disabled={!isClearArmed}
            variant="contained"
            color="error"
            startIcon={<DeleteForeverIcon />}
            sx={{ textTransform: "none" }}
          >
            {clearLoading ? "Deleting…" : "Delete Permanently"}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

export default AdminAuditLog;
